import { inject, Injectable } from "@angular/core";
import { GraphService } from "@kernel/graphql/services/graph.service";
import { map } from "rxjs";
import { STOCK_TYPE_QUERY } from "../queries/stock-type.query";
import { IStockType } from "@app/shared/settings/stock-type/interfaces/stock-type.interface";

/**
 * Provides an abstraction layer for performing GraphQL operations
 * related to stock types, including queries and mutations.
 */
@Injectable({ providedIn: "root" })
export class StockTypeApiService {
  private graphql = inject(GraphService);

  /**
   * Fetches a single StockType by its ID using a GraphQL query.
   *
   * @param id - The unique identifier of the StockType.
   * @returns An observable emitting the retrieved StockType or throwing the first error if any.
   */
  getStockType(id: string) {
    return this.graphql.constructQuery<{ stockType: IStockType }>([STOCK_TYPE_QUERY], "stockType", { id }).pipe(
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data.stockType;
      })
    );
  }

  /**
   * Sends a GraphQL mutation to enable a StockType by ID.
   *
   * @param id - The unique identifier of the StockType to enable.
   * @returns An observable emitting the updated StockType with its enabled status.
   */
  enableStockType(id: string) {
    return this.graphql.constructMutation<{ enableStockType: IStockType }>("enableStockType", { id: "ID!" }, { id }, ["id", "enabled"]).pipe(
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data?.enableStockType;
      })
    );
  }

  /**
   * Sends a GraphQL mutation to disable a StockType by ID.
   *
   * @param id - The unique identifier of the StockType to disable.
   * @returns An observable emitting the updated StockType after disabling it.
   */
  disableStockType(id: string) {
    return this.graphql.constructMutation<{ disableStockType: IStockType }>("disableStockType", { id: "ID!" }, { id }, ["id", "enabled"]).pipe(
      map(data => {
        if (data.errors?.length) {
          throw data.errors[0];
        }
        return data.data?.disableStockType;
      })
    );
  }
}
